import React, { useState, useEffect } from 'react';
import { Gift, Plus, Search, DollarSign, Calendar, User, Mail, CreditCard, Settings, Trash2, AlertTriangle, Copy, CheckCircle, Phone, Tag } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import GiftCardsManagementModal from '../components/GiftCardsManagementModal';
import GiftCardSettingsModal from '../components/GiftCardSettingsModal';
import CreateGiftCardModal from '../components/CreateGiftCardModal';
import GiftCardEmailSettingsModal from '../components/GiftCardEmailSettingsModal';
import GiftCardPromotionsModal from '../components/GiftCardPromotionsModal';
import { GiftCardSettings } from '../services/giftCardSettingsService';

interface GiftCard {
  id: string;
  code: string;
  initial_amount: number;
  current_balance: number;
  purchaser?: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
  };
  recipient_name?: string;
  recipient_email?: string;
  status: 'active' | 'redeemed' | 'expired';
  created_at: string;
  expires_at?: string;
}

export default function GiftCards() {
  const { selectedBusiness } = useBusiness();
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'redeemed' | 'expired'>('all');
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<GiftCard | null>(null);
  const [deleteConfirmCode, setDeleteConfirmCode] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isManagementModalOpen, setIsManagementModalOpen] = useState(false);
  const [isDesignModalOpen, setIsDesignModalOpen] = useState(false);
  const [isEmailSettingsModalOpen, setIsEmailSettingsModalOpen] = useState(false);
  const [isPromotionsModalOpen, setIsPromotionsModalOpen] = useState(false);
  const [editingDesign, setEditingDesign] = useState<GiftCardSettings | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [copied, setCopied] = useState(false);
  
  // Get the public gift card page URL
  const giftCardUrl = selectedBusiness?.slug 
    ? `${window.location.origin}/${selectedBusiness.slug}/gift-cards`
    : '';

  useEffect(() => {
    if (selectedBusiness?.id) {
      loadGiftCards();
    }
  }, [selectedBusiness?.id]);

  const loadGiftCards = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('gift_cards')
        .select(`
          *,
          purchaser:purchaser_customer_id(
            id,
            first_name,
            last_name,
            email,
            phone
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGiftCards(data || []);
    } catch (error) {
      console.error('Error loading gift cards:', error);
      toast.error('Failed to load gift cards');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (card: GiftCard) => {
    setCardToDelete(card);
    setDeleteConfirmCode('');
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cardToDelete || !selectedBusiness?.id) return;

    // Safety check: Code must match
    if (deleteConfirmCode !== cardToDelete.code) {
      toast.error('Code does not match. Please type the exact code to confirm deletion.');
      return;
    }

    try {
      setIsDeleting(true);

      // Delete gift card (cascade will delete transactions)
      const { error } = await supabase
        .from('gift_cards')
        .delete()
        .eq('id', cardToDelete.id)
        .eq('business_id', selectedBusiness.id);

      if (error) throw error;

      // Update local state
      setGiftCards(prev => prev.filter(c => c.id !== cardToDelete.id));
      
      toast.success(`Gift card ${cardToDelete.code} deleted successfully`);
      setIsDeleteModalOpen(false);
      setCardToDelete(null);
      setDeleteConfirmCode('');
    } catch (error) {
      console.error('Error deleting gift card:', error);
      toast.error('Failed to delete gift card');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredGiftCards = giftCards.filter(card => {
    const purchaserName = card.purchaser ? `${card.purchaser.first_name} ${card.purchaser.last_name}` : '';
    const purchaserEmail = card.purchaser?.email || '';
    const purchaserPhone = card.purchaser?.phone || '';
    
    const matchesSearch = 
      card.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchaserName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchaserEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchaserPhone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.recipient_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || card.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const handleCopyUrl = async () => {
    if (!giftCardUrl) return;
    
    try {
      await navigator.clipboard.writeText(giftCardUrl);
      setCopied(true);
      toast.success('Gift card page URL copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = giftCardUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success('Gift card page URL copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy URL:', fallbackErr);
        toast.error('Failed to copy URL');
      }
      document.body.removeChild(textArea);
    }
  };

  const stats = {
    total: giftCards.length,
    active: giftCards.filter(c => c.status === 'active').length,
    totalValue: giftCards
      .filter(c => c.status === 'active')
      .reduce((sum, c) => sum + c.current_balance, 0),
    redeemed: giftCards.filter(c => c.status === 'redeemed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 pb-6 bg-gray-50" style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
    }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 pt-4 sm:pt-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Gift Cards</h1>
          <p className="text-[15px] text-gray-600 mt-0.5" style={{ letterSpacing: '-0.01em' }}>Manage virtual gift cards for your business</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsManagementModalOpen(true)}
            className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-white active:bg-gray-50 text-gray-700 border border-gray-200/60 rounded-[10px] text-[13px] sm:text-[15px] font-semibold transition-all duration-150 touch-manipulation shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
            style={{ 
              minHeight: '36px',
              WebkitTapHighlightColor: 'transparent',
              letterSpacing: '-0.01em'
            }}
          >
            <Gift className="h-4 w-4" />
            <span className="hidden sm:inline">My Designs</span>
            <span className="sm:hidden">Designs</span>
          </button>
          <button
            onClick={() => setIsPromotionsModalOpen(true)}
            className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-white active:bg-gray-50 text-gray-700 border border-gray-200/60 rounded-[10px] text-[13px] sm:text-[15px] font-semibold transition-all duration-150 touch-manipulation shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
            style={{ 
              minHeight: '36px',
              WebkitTapHighlightColor: 'transparent',
              letterSpacing: '-0.01em'
            }}
          >
            <Tag className="h-4 w-4" />
            <span className="hidden sm:inline">Promotions</span>
            <span className="sm:hidden">Promos</span>
          </button>
          <button
            onClick={() => setIsEmailSettingsModalOpen(true)}
            className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-white active:bg-gray-50 text-gray-700 border border-gray-200/60 rounded-[10px] text-[13px] sm:text-[15px] font-semibold transition-all duration-150 touch-manipulation shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
            style={{ 
              minHeight: '36px',
              WebkitTapHighlightColor: 'transparent',
              letterSpacing: '-0.01em'
            }}
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Settings</span>
            <span className="sm:hidden">Settings</span>
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] text-[13px] sm:text-[15px] font-semibold transition-all duration-150 touch-manipulation shadow-sm"
            style={{ 
              minHeight: '36px',
              WebkitTapHighlightColor: 'transparent',
              letterSpacing: '-0.01em'
            }}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Sell Gift Card</span>
            <span className="sm:hidden">Sell</span>
          </button>
        </div>
      </div>

      {/* Public Gift Card Page URL */}
      {selectedBusiness?.slug && (
        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
          <div className="flex flex-col gap-4">
            <div className="flex-1 min-w-0">
              <label className="block text-[13px] font-semibold text-gray-700 mb-2" style={{ letterSpacing: '-0.01em' }}>
                Public Gift Card Page URL
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  value={giftCardUrl}
                  readOnly
                  className="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200/60 rounded-[10px] text-[13px] text-gray-900 font-mono truncate focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  style={{ minHeight: '44px' }}
                />
                <button
                  onClick={handleCopyUrl}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] text-[13px] font-semibold transition-all duration-150 touch-manipulation whitespace-nowrap shadow-sm"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                  {copied ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-2" style={{ letterSpacing: '-0.01em' }}>
                Share this link with customers so they can purchase gift cards online
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-3 sm:p-4">
          <div className="flex flex-col">
            <p className="text-[13px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>Total Cards</p>
            <p className="text-[20px] font-bold text-gray-900 mt-0.5" style={{ letterSpacing: '-0.02em' }}>{stats.total}</p>
          </div>
        </div>

        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-3 sm:p-4">
          <div className="flex flex-col">
            <p className="text-[13px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>Active Cards</p>
            <p className="text-[20px] font-bold text-gray-900 mt-0.5" style={{ letterSpacing: '-0.02em' }}>{stats.active}</p>
          </div>
        </div>

        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-3 sm:p-4">
          <div className="flex flex-col">
            <p className="text-[13px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>Outstanding Balance</p>
            <p className="text-[20px] font-bold text-gray-900 mt-0.5" style={{ letterSpacing: '-0.02em' }}>
              ${stats.totalValue.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-3 sm:p-4">
          <div className="flex flex-col">
            <p className="text-[13px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>Redeemed</p>
            <p className="text-[20px] font-bold text-gray-900 mt-0.5" style={{ letterSpacing: '-0.02em' }}>{stats.redeemed}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4 sticky top-0 z-10" style={{
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
      }}>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code, name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200/60 rounded-[10px] text-[15px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ 
                minHeight: '44px',
                letterSpacing: '-0.01em'
              }}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2.5 rounded-[10px] text-[13px] font-semibold transition-all duration-150 touch-manipulation ${
                filterStatus === 'all'
                  ? 'bg-blue-600 active:bg-blue-700 text-white'
                  : 'bg-gray-100 active:bg-gray-200 text-gray-700'
              }`}
              style={{ 
                minHeight: '44px',
                WebkitTapHighlightColor: 'transparent',
                letterSpacing: '-0.01em'
              }}
            >
              All
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2.5 rounded-[10px] text-[13px] font-semibold transition-all duration-150 touch-manipulation ${
                filterStatus === 'active'
                  ? 'bg-blue-600 active:bg-blue-700 text-white'
                  : 'bg-gray-100 active:bg-gray-200 text-gray-700'
              }`}
              style={{ 
                minHeight: '44px',
                WebkitTapHighlightColor: 'transparent',
                letterSpacing: '-0.01em'
              }}
            >
              Active
            </button>
            <button
              onClick={() => setFilterStatus('redeemed')}
              className={`px-4 py-2.5 rounded-[10px] text-[13px] font-semibold transition-all duration-150 touch-manipulation ${
                filterStatus === 'redeemed'
                  ? 'bg-blue-600 active:bg-blue-700 text-white'
                  : 'bg-gray-100 active:bg-gray-200 text-gray-700'
              }`}
              style={{ 
                minHeight: '44px',
                WebkitTapHighlightColor: 'transparent',
                letterSpacing: '-0.01em'
              }}
            >
              Redeemed
            </button>
          </div>
        </div>
      </div>

      {/* Gift Cards - Desktop Table */}
      {filteredGiftCards.length === 0 ? (
        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-12 text-center">
          <div className="flex flex-col items-center">
            <Gift className="h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-semibold" style={{ letterSpacing: '-0.01em' }}>No gift cards found</p>
            <p className="text-gray-400 text-[13px] mt-1" style={{ letterSpacing: '-0.01em' }}>
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first gift card to get started'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200/60">
                  <tr>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '-0.01em' }}>
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '-0.01em' }}>
                      Purchaser
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '-0.01em' }}>
                      Recipient
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '-0.01em' }}>
                      Initial Amount
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '-0.01em' }}>
                      Balance
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '-0.01em' }}>
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '-0.01em' }}>
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider" style={{ letterSpacing: '-0.01em' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200/60">
                  {filteredGiftCards.map((card) => (
                    <tr key={card.id} className="active:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CreditCard className="h-4 w-4 text-blue-600 mr-2" />
                          <span className="text-[15px] font-mono font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                            {card.code}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-[15px]">
                          <div className="font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                            {card.purchaser ? `${card.purchaser.first_name} ${card.purchaser.last_name}` : 'N/A'}
                          </div>
                          {card.purchaser?.email && (
                            <div className="text-[13px] text-gray-600 mt-0.5" style={{ letterSpacing: '-0.01em' }}>{card.purchaser.email}</div>
                          )}
                          {card.purchaser?.phone && (
                            <div className="text-[13px] text-gray-600 mt-0.5" style={{ letterSpacing: '-0.01em' }}>{card.purchaser.phone}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-[15px]">
                          <div className="font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                            {card.recipient_name || 'N/A'}
                          </div>
                          <div className="text-[13px] text-gray-600 mt-0.5" style={{ letterSpacing: '-0.01em' }}>{card.recipient_email || ''}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                        ${card.initial_amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                        ${card.current_balance.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 inline-flex text-[11px] font-semibold rounded-[6px] ${
                            card.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : card.status === 'redeemed'
                              ? 'bg-gray-100 text-gray-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                          style={{ letterSpacing: '-0.01em' }}
                        >
                          {card.status.charAt(0).toUpperCase() + card.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                        {new Date(card.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleDeleteClick(card)}
                          className="inline-flex items-center px-3 py-1.5 text-[13px] font-semibold text-red-600 active:text-red-700 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                          style={{ 
                            WebkitTapHighlightColor: 'transparent',
                            minHeight: '36px',
                            letterSpacing: '-0.01em'
                          }}
                          title="Delete gift card"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {filteredGiftCards.map((card) => (
              <div
                key={card.id}
                className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4 space-y-3 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {/* Header Row */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CreditCard className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[15px] font-mono font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                        {card.code}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                        {new Date(card.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`px-2 py-0.5 text-[11px] font-semibold rounded-[6px] ${
                        card.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : card.status === 'redeemed'
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                      style={{ letterSpacing: '-0.01em' }}
                    >
                      {card.status.charAt(0).toUpperCase() + card.status.slice(1)}
                    </span>
                    <button
                      onClick={() => handleDeleteClick(card)}
                      className="p-2 text-red-600 active:text-red-700 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                      style={{ 
                        WebkitTapHighlightColor: 'transparent',
                        minHeight: '44px',
                        minWidth: '44px'
                      }}
                      title="Delete gift card"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Amounts Row */}
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200/60">
                  <div>
                    <div className="text-[11px] text-gray-600 mb-1 font-medium" style={{ letterSpacing: '-0.01em' }}>Initial Amount</div>
                    <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                      ${card.initial_amount.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-600 mb-1 font-medium" style={{ letterSpacing: '-0.01em' }}>Balance</div>
                    <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                      ${card.current_balance.toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Purchaser Info */}
                <div className="pt-2 border-t border-gray-200/60">
                  <div className="text-[11px] text-gray-600 mb-1 font-medium" style={{ letterSpacing: '-0.01em' }}>Purchaser</div>
                  <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                    {card.purchaser ? `${card.purchaser.first_name} ${card.purchaser.last_name}` : 'N/A'}
                  </div>
                  {card.purchaser?.email && (
                    <div className="text-[11px] text-gray-600 mt-1 flex items-center gap-1" style={{ letterSpacing: '-0.01em' }}>
                      <Mail className="h-3 w-3" />
                      {card.purchaser.email}
                    </div>
                  )}
                  {card.purchaser?.phone && (
                    <div className="text-[11px] text-gray-600 mt-1 flex items-center gap-1" style={{ letterSpacing: '-0.01em' }}>
                      <Phone className="h-3 w-3" />
                      {card.purchaser.phone}
                    </div>
                  )}
                </div>

                {/* Recipient Info */}
                {(card.recipient_name || card.recipient_email) && (
                  <div className="pt-2 border-t border-gray-200/60">
                    <div className="text-[11px] text-gray-600 mb-1 font-medium" style={{ letterSpacing: '-0.01em' }}>Recipient</div>
                    {card.recipient_name && (
                      <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                        {card.recipient_name}
                      </div>
                    )}
                    {card.recipient_email && (
                      <div className="text-[11px] text-gray-600 mt-1 flex items-center gap-1" style={{ letterSpacing: '-0.01em' }}>
                        <Mail className="h-3 w-3" />
                        {card.recipient_email}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Management Modal */}
      <GiftCardsManagementModal
        isOpen={isManagementModalOpen}
        onClose={() => setIsManagementModalOpen(false)}
        onCreateNewCard={() => {
          setEditingDesign(null);
          setIsDesignModalOpen(true);
        }}
        onEditCard={(design) => {
          setEditingDesign(design);
          setIsDesignModalOpen(true);
        }}
        refreshTrigger={refreshTrigger}
      />

      {/* Design Modal */}
      <GiftCardSettingsModal
        isOpen={isDesignModalOpen}
        onClose={() => {
          setIsDesignModalOpen(false);
          setEditingDesign(null);
        }}
        editingDesign={editingDesign}
        onSaveSuccess={() => {
          setRefreshTrigger(prev => prev + 1);
        }}
      />

      {/* Create Gift Card Modal */}
      <CreateGiftCardModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          loadGiftCards(); // Refresh the gift cards list
        }}
      />

      <GiftCardEmailSettingsModal
        isOpen={isEmailSettingsModalOpen}
        onClose={() => setIsEmailSettingsModalOpen(false)}
      />

      {/* Gift Card Promotions Modal */}
      <GiftCardPromotionsModal
        isOpen={isPromotionsModalOpen}
        onClose={() => setIsPromotionsModalOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && cardToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[12px] shadow-xl max-w-md w-full p-6" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
          }}>
            {/* Warning Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Gift Card?</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            {/* Gift Card Details */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Code:</span>
                  <span className="font-mono font-semibold text-gray-900">{cardToDelete.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Balance:</span>
                  <span className="font-semibold text-gray-900">${cardToDelete.current_balance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-semibold ${
                    cardToDelete.status === 'active' ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {cardToDelete.status.charAt(0).toUpperCase() + cardToDelete.status.slice(1)}
                  </span>
                </div>
                {cardToDelete.purchaser && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Purchaser:</span>
                    <span className="font-semibold text-gray-900">
                      {cardToDelete.purchaser.first_name} {cardToDelete.purchaser.last_name}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Warning Messages */}
            <div className="space-y-3 mb-6">
              <div className="flex items-start space-x-2 text-sm text-red-700 bg-red-50 p-3 rounded-lg">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Warning: This will permanently delete:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1 text-red-600">
                    <li>The gift card code and balance</li>
                    <li>All transaction history</li>
                    <li>Any redemption records</li>
                  </ul>
                </div>
              </div>

              {cardToDelete.current_balance > 0 && cardToDelete.status === 'active' && (
                <div className="flex items-start space-x-2 text-sm text-orange-700 bg-orange-50 p-3 rounded-lg">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
                    <strong>This card has a balance of ${cardToDelete.current_balance.toFixed(2)}!</strong> 
                    <br />Consider refunding the purchaser before deleting.
                  </p>
                </div>
              )}
            </div>

            {/* Confirmation Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type the gift card code <span className="font-mono font-semibold text-red-600">{cardToDelete.code}</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmCode}
                onChange={(e) => setDeleteConfirmCode(e.target.value.toUpperCase())}
                placeholder="Enter gift card code"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono"
                autoFocus
              />
              {deleteConfirmCode && deleteConfirmCode !== cardToDelete.code && (
                <p className="text-xs text-red-600 mt-1">Code does not match</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setCardToDelete(null);
                  setDeleteConfirmCode('');
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 text-[15px] font-semibold text-gray-700 bg-white border border-gray-200/60 active:bg-gray-50 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation shadow-sm"
                style={{ 
                  minHeight: '44px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting || deleteConfirmCode !== cardToDelete.code}
                className="flex-1 px-4 py-2.5 text-[15px] font-semibold text-white bg-red-600 active:bg-red-700 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation flex items-center justify-center space-x-2 shadow-sm"
                style={{ 
                  minHeight: '44px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em'
                }}
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Permanently</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

